import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import Pane from 'components/Common/Pane'
import AlwaysOnRateOptionMobile from '../AlwaysOnRateOptionMobile'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import config from 'constants/config'
import SinglePropertyPackageSelectionModal from './SinglePropertyPackageSelectionModal'
import AccommodationMobileHeaderFilters from 'components/OfferPage/Accommodation/AccommodationMobileHeaderFilters'
import BodyText from 'components/Luxkit/Typography/BodyText'

const OptionsContainer = styled.div`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType;
  offer: App.Offer;
  airports?: Array<App.Airport>;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
  onBack?: () => void;
}

function AlwaysOnPackageSelectionModal(props: Props) {
  const {
    room,
    offer,
    airports,
    packageOptions,
    onClose,
    onBack,
  } = props

  const [propertyMapping] = usePropertyOfferMapping(offer.property?.id, {
    disabled: !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions)
  }, [packageOptions])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  if (propertyMapping) {
    return <SinglePropertyPackageSelectionModal onBack={onBack} onClose={onClose} />
  }

  return (
    <>
      <ModalHeader
        title="Select a package"
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      >
        <AccommodationMobileHeaderFilters />
      </ModalHeader>
      <ModalBody>
        <Pane background="neutral-six">
          <Group direction="vertical" gap={8}>
            {packageViews.length > 1 && (
              <OptionsContainer>
                <BodyText variant="small" colour="neutral-three">{pluralizeToString('package option', packageViews.length)} available</BodyText>
              </OptionsContainer>
            )}
            {packageViews.map((view, index) => <Pane key={view.package.uniqueKey}>
              <AlwaysOnRateOptionMobile
                key={view.package.uniqueKey}
                offer={offer}
                optionNumber={index + 1}
                packageView={view}
                flightDestination={destinationAirport}
                showOptionLabel={packageViews.length > 1}
              />
            </Pane>)}
          </Group>
        </Pane>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  rooms: state.rooms,
  airports: appState.geo.airports,
  packageOptions: getDurationPackageOptions(state, appState),
}))(AlwaysOnPackageSelectionModal)
